import React, { useCallback, useEffect, useMemo } from "react";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { Alert, Button, CircularProgress } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpProspectStatusEnum,
  useCreateCmpProspectLeadCaptureMutation,
  useGetCmpProspectDetailQuery,
  useGetCmpProspectLeadCapturesQuery,
  useUpdateCmpProspectMutation,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import { ProspectForm, ProspectFormAction } from "../ProspectForm";
import {
  mapProspectToFormValues,
  ProspectFieldsInput,
  ProspectSaveResult,
} from "../prospectFormModel";

type UpdateProspectLocationState = {
  prospectId?: string;
  cmpProspectId?: string;
};

const formatRelativeUpdatedTime = (timestamp?: number | null): string => {
  if (!timestamp) {
    return "";
  }
  const elapsedMilliseconds = Math.max(0, Date.now() - timestamp);
  const elapsedDays = Math.floor(elapsedMilliseconds / (24 * 60 * 60 * 1000));

  if (elapsedDays === 0) {
    return "today";
  }
  if (elapsedDays === 1) {
    return "1 day ago";
  }
  return `${elapsedDays} days ago`;
};

const formatUpdatedTimestamp = (timestamp?: number | null): string => {
  if (!timestamp) {
    return "";
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const dateLabel = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  return `${dateLabel} at ${timeLabel}`;
};

const LastUpdatedHeader = ({ timestamp }: { timestamp?: number | null }) => {
  if (!timestamp) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 text-right text-xs leading-5 text-gray-800">
      <div>
        <div className="font-bold">
          Last Updated {formatRelativeUpdatedTime(timestamp)}
        </div>
        <div>{formatUpdatedTimestamp(timestamp)}</div>
      </div>
      <AccessTimeOutlinedIcon sx={{ color: "#6B7280", fontSize: 22 }} />
    </div>
  );
};

const UpdateProspect = () => {
  const { prospectId: prospectIdFromPath } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const setPageHeaderOverride = usePageHeaderOverride();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const locationState = location.state as UpdateProspectLocationState | null;
  const prospectId =
    prospectIdFromPath ||
    locationState?.prospectId ||
    locationState?.cmpProspectId ||
    new URLSearchParams(location.search).get("prospectId") ||
    "";

  const {
    data: detailData,
    loading: detailLoading,
    error: detailError,
  } = useGetCmpProspectDetailQuery({
    variables: { input: { chamberId, prospectId } },
    skip: !chamberId || !prospectId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: leadCaptureData,
    loading: leadCapturesLoading,
    error: leadCapturesQueryError,
  } = useGetCmpProspectLeadCapturesQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const [updateProspect] = useUpdateCmpProspectMutation();
  const [createLeadCapture] = useCreateCmpProspectLeadCaptureMutation();
  const prospect = detailData?.getCmpProspectDetail;

  const headerActions = useMemo(
    () => <LastUpdatedHeader timestamp={prospect?.updatedAtUtcMilli} />,
    [prospect?.updatedAtUtcMilli],
  );

  useEffect(() => {
    setPageHeaderOverride({
      title: "Update Prospect Member",
      description:
        "Use this section to update a prospect’s information. Click update status from prospect to complete and submit a member application who has choose to become a member.",
      actions: headerActions,
      withHeaderDivider: true,
    });
    return () => setPageHeaderOverride(null);
  }, [headerActions, setPageHeaderOverride]);

  const leadCaptures = useMemo(
    () =>
      (leadCaptureData?.getCmpProspectLeadCaptures || []).map(
        (leadCapture: { id: string; label: string }) => ({
          id: leadCapture.id,
          label: leadCapture.label,
        }),
      ),
    [leadCaptureData],
  );

  const handleCreateLeadCapture = useCallback(
    async (label: string) => {
      if (!chamberId) {
        throw new Error("Chamber information is still loading. Try again.");
      }

      const response = await createLeadCapture({
        variables: { input: { chamberId, label } },
      });
      const payload = response.data?.createCmpProspectLeadCapture;
      if (!payload?.leadCaptureSavedSuccessfully || !payload.leadCapture) {
        throw new Error("The lead capture could not be saved.");
      }

      return {
        id: payload.leadCapture.id,
        label: payload.leadCapture.label,
      };
    },
    [chamberId, createLeadCapture],
  );

  const handleSave = useCallback(
    async (data: ProspectFieldsInput) => {
      if (!chamberId || !prospectId) {
        throw new Error("Prospect information is still loading. Try again.");
      }

      const response = await updateProspect({
        variables: { input: { chamberId, prospectId, data } },
      });
      const payload = response.data?.updateCmpProspect;
      if (!payload?.prospectSavedSuccessfully || !payload.prospect) {
        throw new Error("The prospect could not be updated.");
      }

      return payload.prospect;
    },
    [chamberId, prospectId, updateProspect],
  );

  const handleSaved = useCallback(
    (savedProspect: ProspectSaveResult, action: ProspectFormAction) => {
      if (action === "convert") {
        navigate(
          `${RoutePaths.chamberAdmin_members_create}?prospectId=${encodeURIComponent(
            savedProspect.id,
          )}`,
        );
      }
    },
    [navigate],
  );

  if (!prospectId) {
    return (
      <Alert severity="error">
        No prospect was selected. Return to Manage Prospects and choose a
        prospect to update.
      </Alert>
    );
  }

  if ((!chamberId || detailLoading) && !prospect) {
    return (
      <div className="flex min-h-[320px] items-center justify-center gap-3 text-sm font-semibold text-gray-700">
        <CircularProgress size={24} />
        Loading prospect...
      </div>
    );
  }

  if (detailError || !prospect) {
    return (
      <Alert severity="error">
        {detailError?.message || "The prospect could not be loaded."}
      </Alert>
    );
  }

  if (prospect.status === CmpProspectStatusEnum.Converted) {
    return (
      <Alert
        severity="info"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => navigate(RoutePaths.chamberAdmin_prospects_manage)}
          >
            Manage Prospects
          </Button>
        }
      >
        This prospect has already been converted to a member and can no longer
        be updated.
      </Alert>
    );
  }

  return (
    <ProspectForm
      key={prospect.id}
      mode="update"
      initialValues={mapProspectToFormValues(prospect)}
      leadCaptures={leadCaptures}
      leadCapturesLoading={leadCapturesLoading}
      leadCapturesError={
        leadCapturesQueryError
          ? "Lead capture options could not be loaded. You can try again or add a new lead capture."
          : undefined
      }
      cancelPath={RoutePaths.chamberAdmin_prospects_manage}
      onCreateLeadCapture={handleCreateLeadCapture}
      onSave={handleSave}
      onSaved={handleSaved}
    />
  );
};

export default UpdateProspect;
